/**
 * SplashScreen — abertura da URL inicial "/" com animação 3D de I.A.
 *
 * Fluxo: "/" → splash (esfera Sphere em WebGL + marca Sphere) → só avança para /login quando o
 * usuário clica em "Continuar" (SEM auto-avanço). Rota pública, fora do AuthGuard.
 *
 * Desempenho: a cena 3D (three.js) é carregada via React.lazy — só entra no
 * bundle nesta tela. Acessibilidade: respeita prefers-reduced-motion (não
 * carrega 3D, mostra fallback estático com logo + backdrop).
 */
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '@/theme/palette';
import { NavaBackdrop } from '@/components/ui/NavaBackdrop';
import { NavaWordmark } from '@/components/ui/NavaWordmark';
import { PRODUCT_TAGLINE } from '@/theme/branding';

import { HeroSphere } from '@/ui/sphere/SphereCanvas';
import '@/ui/sphere/sphere.css';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function SplashScreen() {
  const navigate = useNavigate();
  const reduced = useRef(prefersReducedMotion()).current;

  const goLogin = () => navigate('/login', { replace: true });

  return (
    <div
      style={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: C.black,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingBottom: '16vh',
      }}
    >
      <NavaBackdrop />

      {/* Esfera de energia (WebGL, carregada sob demanda). Com movimento reduzido ou
          sem WebGL, a própria HeroSphere mostra o quadro estático / a imagem. */}
      <HeroSphere className="sp-hero-art sp-splash-art" />

      {/* Marca + tagline + CTA sobre a cena */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <div
          style={{
            animation: reduced ? 'none' : 'navaFadeUp 1.2s ease-out both',
            pointerEvents: 'none',
          }}
        >
          <NavaWordmark size={64} />
        </div>
        <div
          style={{
            marginTop: 18,
            marginBottom: 28,
            color: C.neutralDim,
            fontSize: 14,
            letterSpacing: '0.08em',
            maxWidth: 520,
            pointerEvents: 'none',
            animation: reduced ? 'none' : 'navaFadeUp 1.6s ease-out both',
          }}
        >
          {PRODUCT_TAGLINE}
        </div>

        <button
          type="button"
          onClick={goLogin}
          autoFocus
          style={{
            padding: '14px 32px',
            fontSize: 16,
            fontWeight: 600,
            color: C.black,
            backgroundColor: C.cyber,
            border: `1px solid ${C.cyber}`,
            borderRadius: 8,
            cursor: 'pointer',
            letterSpacing: '-0.01em',
            transition: 'all 150ms',
            animation: reduced ? 'none' : 'navaFadeUp 2s ease-out both',
          }}
          className="inline-flex items-center gap-2 hover:opacity-90"
          aria-label="Continuar — ir para o login"
        >
          Continuar →
        </button>
      </div>
    </div>
  );
}
